import ConditionalWrapper from "@components/common/ConditionalWrapper";
import { slashBeforeUrlAdder } from "@components/utils/urlHelpers";
import { SliderContext } from "@libs/slider/SliderCarousel";
import { ITag } from "@modules/requestAQuote/components/TripPreferences";
import Link from "next/link";
import { useContext, useMemo } from "react";
import CardBody from "./components/CardBody";
import CardHeader from "./components/CardHeader";

import { IImageComponentResponse } from "@customTypes/componentResponses";
import { CardTypeType } from "./utils/constants";

export type HeadingLevelType = "h2" | "h3" | "h4";

interface ICard {
  id: number;
  url?: string;
  opensNewTab?: boolean;
  image?: string | IImageComponentResponse | number | null;
  tags?: ITag[];
  hasLike?: boolean;
  title?: string;
  isTitleLocation?: boolean;
  subtitle?: string;
  blurb?: string;
  priceRate?: number;
  tagLeftTag?: ITag;
  linkTitle?: string;
  imageSize?: string;
  isHorizontal?: boolean;
  cardType?: CardTypeType;
  imageLoadingPriority?: boolean;
  imageWidth?: number;
  headingLevel?: HeadingLevelType;
}

const Card = ({
  id,
  url,
  opensNewTab,
  image,
  tags,
  hasLike,
  title,
  isTitleLocation,
  subtitle,
  blurb,
  priceRate,
  tagLeftTag,
  linkTitle,
  imageSize,
  isHorizontal,
  cardType,
  imageLoadingPriority = false,
  imageWidth,
  headingLevel,
}: ICard): JSX.Element => {
  const { preventCarouselClick }: any = useContext(SliderContext);

  const noImage = useMemo(() => {
    return imageSize === "none";
  }, [imageSize]);

  return (
    <ConditionalWrapper
      condition={!!url}
      wrapper={(children) => (
        <Link
          className="w-full"
          onClick={(e) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            preventCarouselClick ? preventCarouselClick(e) : undefined;
          }}
          href={url ? slashBeforeUrlAdder(url) : ""}
          target={opensNewTab ? "_blank" : undefined}
          tabIndex={-1}
        >
          {children}
        </Link>
      )}
    >
      <div className={`flex ${isHorizontal ? "flex-row gap-8" : "flex-col"}`}>
        {!noImage && (
          <ConditionalWrapper
            condition={isHorizontal}
            wrapper={(children) => (
              <div className="min-w-[300px]">{children}</div>
            )}
          >
            <CardHeader
              id={id}
              title={title}
              image={image}
              tags={tags}
              topLeftTag={tagLeftTag}
              imageSize={imageSize}
              imageLoadingPriority={imageLoadingPriority}
              imageWidth={imageWidth}
            />
          </ConditionalWrapper>
        )}

        <CardBody
          title={title}
          isTitleLocation={isTitleLocation}
          location={subtitle}
          blurb={blurb}
          priceRate={priceRate}
          linkTitle={linkTitle}
          noImage={noImage}
          headingLevel={headingLevel}
        />
      </div>
    </ConditionalWrapper>
  );
};

export default Card;
